import { AuthService } from '../../../auth/auth.service';
import { UserAdminWarningConfigService } from '../../services/user-admin-warning-config.service';

import { GetWarnLevelPipe } from './pipes/get-warn-level.pipe';
import { IsParameterAllowedPipe } from './pipes/is-parameter-allowed.pipe';

import { CommonModule, KeyValue } from '@angular/common';
import {
  Component,
  inject,
  input,
  OnChanges,
  OnInit,
  output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatSnackBar } from '@angular/material/snack-bar';

import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';

import { isNil } from '../../../../utils/utils';
import { getWarnLevel } from './constants/constants';
import { WarnParameter } from './enums/warn-parameter.enum';

export interface WarnThresold {
  parameter: WarnParameter;
  level: number;
}

const MODULES = [CommonModule, MatCardModule, TranslateModule, MatButtonModule];
const COMPONENTS = [FaIconComponent];
const PIPES = [IsParameterAllowedPipe, GetWarnLevelPipe];

@Component({
  selector: 'app-user-admin-warning-tab',
  templateUrl: './user-admin-warning-tab.component.html',
  styleUrls: ['user-admin-warning-tab.component.scss'],
  imports: [...MODULES, ...COMPONENTS, ...PIPES],
})
export class UserAdminWarningTabComponent implements OnInit, OnChanges {
  private auth = inject(AuthService);
  private snackBar = inject(MatSnackBar);
  private translate = inject(TranslateService);
  private warningConfig = inject(UserAdminWarningConfigService);

  thresholds = input<WarnThresold[]>();
  info = input<boolean>();
  saving = input<boolean>(false);
  changeThresholds = output<WarnThresold[]>();
  changeInfo = output<boolean>();

  thresholdsToEdit = signal<WarnThresold[]>(null);
  infoToEdit = signal(false);

  readonly levels = this.warningConfig.levels;
  readonly levelIcons = this.warningConfig.levelIcons;

  allowedParameters = signal<string[]>([]);

  ngOnInit() {
    this.setAllowedWarnParameters();

    if (isNil(this.thresholds())) {
      this.setStandardThresholds();
    } else {
      this.thresholdsToEdit.set([...this.thresholds()]);
    }

    this.infoToEdit.set(!isNil(this.info()) && this.info());
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes.hasOwnProperty('thresholds') &&
      !isNil(changes.thresholds.currentValue)
    ) {
      this.thresholdsToEdit.set([...this.thresholds()]);
    }

    if (changes.hasOwnProperty('info') && !isNil(changes.info.currentValue)) {
      this.infoToEdit.set(this.info());
    }
  }

  compare = (
    a: KeyValue<string, unknown>,
    b: KeyValue<string, unknown>
  ): number =>
    (this.warningConfig.order[a.key] ?? Number.MAX_SAFE_INTEGER) -
    (this.warningConfig.order[b.key] ?? Number.MAX_SAFE_INTEGER);

  setStandardThresholds(): void {
    this.thresholdsToEdit.set(this.warningConfig.createStandardThresholds());
  }

  protected readonly WarnParameter = WarnParameter;

  private setAllowedWarnParameters(): void {
    const warnProducts: any[] =
      this.auth.getCustomerDocument()['egwSummerProducts'] ?? [];

    const allowedParameters = this.warningConfig.getAllowedParameters(
      warnProducts,
      productId =>
        this.auth.isProductActive('egwSummerProducts', productId, false, true)
    );

    this.allowedParameters.set(allowedParameters);
  }

  async onSelectLevel(p: WarnParameter | string, level: number): Promise<void> {
    const warnParameter = p as WarnParameter;

    if (
      this.saving() ||
      getWarnLevel(warnParameter, this.thresholdsToEdit()) === level
    ) {
      return;
    }

    try {
      this.patchThresholdsToEdit(warnParameter, level);
      this.changeThresholds.emit(this.thresholdsToEdit());
      this.showSavedMessage();
    } catch (err: any) {
      this.showErrorMessage(err);
    }
  }

  async onChangeWarnInfo(newValue: boolean): Promise<void> {
    if (this.saving() || this.infoToEdit() === newValue) {
      return;
    }

    try {
      this.infoToEdit.set(newValue);
      this.changeInfo.emit(newValue);
      this.showSavedMessage();
    } catch (err: any) {
      this.showErrorMessage(err);
    }
  }

  patchThresholdsToEdit(warnParameter: WarnParameter, level: number): void {
    this.thresholdsToEdit.update(thresholds =>
      thresholds.map(t => (t.parameter === warnParameter ? { ...t, level } : t))
    );
  }

  private showSavedMessage(): void {
    const message = this.translate.instant(
      'PRODUCT.USERADMIN.THRESHOLDS.SAVED'
    );

    this.snackBar.open(message, 'OK', {
      duration: 2000,
      panelClass: ['snackbar-dark-success'],
    });
  }

  private showErrorMessage(error: any): void {
    this.snackBar.open(`Fehler: ${error?.message ?? error}`, 'Schließen', {
      duration: 4000,
    });
  }
}
